(() => {
  if (window.MuzeWaitlist?.initialized) {
    window.MuzeWaitlist.init?.();
    return;
  }

  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const getTrigger = (cta) =>
    cta.querySelector(':scope > add-to-cart-component button[ref="addToCartButton"], :scope > span button[name="add"], button[name="add"]');
  const getPanel = (cta) => cta.querySelector('[data-waitlist-panel]');
  const getEmailInput = (cta) => cta.querySelector('input[name="contact[email]"]');

  const setExpanded = (cta, expanded, focusInput = false) => {
    const trigger = getTrigger(cta);
    const panel = getPanel(cta);
    const emailInput = getEmailInput(cta);

    if (!trigger || !panel) return;

    trigger.setAttribute('type', 'button');
    trigger.setAttribute('data-waitlist-trigger', '');
    trigger.setAttribute('aria-controls', panel.id);
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    cta.dataset.expanded = expanded ? 'true' : 'false';
    panel.setAttribute('aria-hidden', expanded ? 'false' : 'true');

    if (expanded && focusInput && emailInput) {
      requestAnimationFrame(() => emailInput.focus());
    }
  };

  const closeAllDesktopPanels = (eventTarget) => {
    if (isMobile()) return;
    document.querySelectorAll('[data-waitlist-cta][data-expanded="true"]').forEach((cta) => {
      if (!cta.contains(eventTarget)) {
        setExpanded(cta, false);
      }
    });
  };

  const initCta = (cta) => {
    if (!(cta instanceof HTMLElement)) return;
    setExpanded(cta, cta.dataset.expanded === 'true' || !!cta.querySelector('[data-waitlist-status]'));
  };

  const initWaitlistCtas = (root = document) => {
    if (!(root instanceof Document) && !(root instanceof HTMLElement)) return;

    if (root instanceof HTMLElement && root.matches('[data-waitlist-cta]')) {
      initCta(root);
    }

    root.querySelectorAll('[data-waitlist-cta]').forEach(initCta);
  };

  window.MuzeWaitlist = {
    initialized: true,
    init: initWaitlistCtas,
  };

  initWaitlistCtas();

  document.addEventListener('shopify:section:load', (event) => {
    initWaitlistCtas(event.target);
  });

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) initWaitlistCtas(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-waitlist-trigger], [data-waitlist-cta] add-to-cart-component button[ref="addToCartButton"], [data-waitlist-cta] button[name="add"]');
    if (trigger) {
      const cta = trigger.closest('[data-waitlist-cta]');
      if (!cta) return;

      event.preventDefault();
      const expanded = cta.dataset.expanded === 'true';
      setExpanded(cta, !expanded, false);
      return;
    }

    const closeButton = target.closest('[data-waitlist-close]');
    if (closeButton) {
      const cta = closeButton.closest('[data-waitlist-cta]');
      if (!cta) return;

      event.preventDefault();
      scheduleCaptchaCleanup();
      runAggressiveCleanupWindow();
      setExpanded(cta, false);
      getTrigger(cta)?.focus();
      return;
    }

    closeAllDesktopPanels(target);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    document.querySelectorAll('[data-waitlist-cta][data-expanded="true"]').forEach((cta) => {
      scheduleCaptchaCleanup();
      runAggressiveCleanupWindow();
      setExpanded(cta, false);
      getTrigger(cta)?.focus();
    });
  });

  const CAPTCHA_KEYWORDS = ['captcha', 'hcaptcha', 'recaptcha', 'turnstile', 'challenge'];
  let aggressiveCleanupTimer = null;
  let cleanupQueued = false;

  const includesCaptchaKeyword = (value) => {
    const normalized = String(value || '').toLowerCase();
    return CAPTCHA_KEYWORDS.some((keyword) => normalized.includes(keyword));
  };

  const hideCaptchaNode = (node) => {
    if (!(node instanceof HTMLElement)) return;
    node.style.setProperty('opacity', '0', 'important');
    node.style.setProperty('pointer-events', 'none', 'important');
    node.style.setProperty('position', 'fixed', 'important');
    node.style.setProperty('right', '-9999px', 'important');
    node.style.setProperty('bottom', '-9999px', 'important');
    node.style.setProperty('z-index', '-9999', 'important');
    node.style.removeProperty('display');
    node.style.removeProperty('visibility');
  };

  const hideFloatingBadge = (element) => {
    if (!(element instanceof HTMLElement)) return;

    const computed = window.getComputedStyle(element);
    if (computed.position !== 'fixed') return;

    const zIndex = Number.parseInt(computed.zIndex || '0', 10);
    if (!Number.isFinite(zIndex) || zIndex < 999) return;

    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const nearViewportEdge =
      window.innerWidth - rect.right < 56 || window.innerHeight - rect.bottom < 56 || rect.left < 56;
    const badgeSize = rect.width <= 360 && rect.height <= 180;
    
    // Only target the small floating badge, NOT the large challenge modal popup
    if (!nearViewportEdge || !badgeSize) return;

    const frame = element.querySelector('iframe');
    const frameSrc = frame?.getAttribute('src') || '';
    const frameTitle = frame?.getAttribute('title') || '';
    const containerHints = [element.id, element.className, element.getAttribute('aria-label'), computed.content];

    const looksLikeCaptchaContainer =
      !!frame ||
      !!element.shadowRoot ||
      containerHints.some((hint) => includesCaptchaKeyword(hint)) ||
      includesCaptchaKeyword(frameSrc) ||
      includesCaptchaKeyword(frameTitle);

    if (looksLikeCaptchaContainer) {
      // DO NOT REMOVE the element from the DOM. Shopify needs it to generate the token and submit the form.
      // Only hide it visually.
      hideCaptchaNode(element);
    }
  };

  const purgeCaptcha = () => {
    document.querySelectorAll('.grecaptcha-badge, .hcaptcha-badge').forEach((el) => {
      hideCaptchaNode(el);
    });

    document.querySelectorAll('body > div, body > aside').forEach((element) => {
      hideFloatingBadge(element);
    });
  };

  const scheduleCaptchaCleanup = () => {
    if (cleanupQueued) return;
    cleanupQueued = true;
    requestAnimationFrame(() => {
      cleanupQueued = false;
      purgeCaptcha();
    });
  };

  const runAggressiveCleanupWindow = () => {
    if (aggressiveCleanupTimer) {
      clearInterval(aggressiveCleanupTimer);
    }

    let runs = 0;
    aggressiveCleanupTimer = window.setInterval(() => {
      runs += 1;
      purgeCaptcha();
      if (runs >= 120) {
        clearInterval(aggressiveCleanupTimer);
        aggressiveCleanupTimer = null;
      }
    }, 60);
  };

  scheduleCaptchaCleanup();

  const captchaObserver = new MutationObserver((mutationList) => {
    for (const mutation of mutationList) {
      if (mutation.type === 'attributes') {
        const target = mutation.target;
        if (target instanceof Element && includesCaptchaKeyword(`${target.id} ${target.className}`)) {
          scheduleCaptchaCleanup();
          runAggressiveCleanupWindow();
          return;
        }
      }

      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (
          includesCaptchaKeyword(`${node.id} ${node.className} ${node.getAttribute('title') || ''}`) ||
          node.matches?.('.grecaptcha-badge, .hcaptcha-badge') ||
          node.querySelector?.('.grecaptcha-badge, .hcaptcha-badge')
        ) {
          hideCaptchaNode(node);
          node.querySelectorAll?.('.grecaptcha-badge, .hcaptcha-badge').forEach((el) => hideCaptchaNode(el));
          scheduleCaptchaCleanup();
          runAggressiveCleanupWindow();
          return;
        }
      }
    }
  });

  captchaObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['id', 'class', 'style', 'src', 'title']
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-waitlist-trigger], [data-waitlist-panel]')) {
      scheduleCaptchaCleanup();
      runAggressiveCleanupWindow();
    }
  });

})();